/**
 * Formatea una cadena reemplazando los marcadores de posición con los valores correspondientes de un mapa de reemplazo.
 *
 * @param unformattedString - La cadena que contiene marcadores de posición en el formato `{key}`.
 * @param replacementMap - Un objeto donde las claves corresponden a los marcadores de posición en la cadena y los valores son los reemplazos.
 * @returns La cadena formateada con los marcadores de posición reemplazados por sus valores correspondientes del mapa de reemplazo.
 *
 * @example
 * ```typescript
 * const template = "Hola, {name}!";
 * const replacements = { name: "Alicia" };
 * const result = format(template, replacements);
 * console.log(result); // "Hola, Alicia!"
 * ```
 */
export function format(
  unformattedString: string,
  replacementMap: Record<string, string>
): string {
  return unformattedString.replace(
    /{(\w+)}/g,
    (_, key: string) => replacementMap[key] ?? `{${key}}`
  );
}

/**
 * Verifica si una cadena es nula, indefinida o está vacía.
 * @param {string | null | undefined} value - La cadena a verificar.
 * @returns {boolean} true si la cadena es nula, indefinida o está vacía; de lo contrario, false.
 */
export function isEmpty(value: string | null | undefined): boolean {
  return value === null || value === undefined || value.trim() === '';
}

/**
 * Reemplaza los marcadores de posición en una cadena con los valores proporcionados.
 * Los marcadores de posición deben estar en el formato `{{name}}`.
 *
 * @param {string} message - La cadena que contiene los marcadores de posición.
 * @param {Array<{ name: string; value: string }>} values - Un arreglo de objetos que contienen el nombre del marcador de posición y su valor correspondiente.
 * @returns {string} La cadena resultante con los marcadores de posición reemplazados por sus valores correspondientes.
 */
export function interpolateStr(
  message: string,
  values?: { name: string; value: string }[]
): string {
  let result = message;
  if (values) {
    values.forEach((item) => {
      result = result.replace(`{{${item.name}}}`, item.value);
    });
  }
  return result;
}

/**
 * Rellena un número con ceros a la izquierda para que tenga al menos 2 dígitos.
 * @param value - El número a rellenar.
 * @returns La cadena resultante con el número rellenado con ceros a la izquierda.
 *
 * @example
 * ```typescript
 * const result = padNumber(5);
 * console.log(result); // "05"
 * ```
 */
export function padNumber(value: number): string {
  return value.toString().padStart(2, '0');
}
